import { faCube, faFiles } from "@fortawesome/pro-regular-svg-icons";

import { getSchemaTypeIcon } from "src/base-components/SchemaTypeSelector";
import { CaseQueueColumnSchema } from "src/cases/queues/types";
import {
  CasePropertiesValue,
  CaseTemplateAdditionalInput,
  CaseTemplateFileCategoryDefinition,
} from "src/clients/case-management";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { EVENT_FALLBACK_ICON, getEventIcon } from "src/eventsCatalogue/utils";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";

type CaseProperty =
  | CasePropertiesValue
  | CaseTemplateAdditionalInput
  | CaseTemplateFileCategoryDefinition;

export const useGetCasePropertyIcon = () => {
  const featureGates = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: {
      enabled: featureGates.entitiesEnabled,
    },
  });
  const { data: eventSchemas } = useEventSchemas();

  return (property: CaseProperty | CaseQueueColumnSchema) => {
    if (!isPropertyWithType(property)) {
      // if no type, it's a file category
      return faFiles;
    }

    if (property.type === "entity_ref" && "entity_type" in property) {
      const entityType = findSchema(property.entity_type, entitySchemas);

      return entityType ? getSchemaIcon(entityType) : faCube;
    }

    if (property.type === "event_ref" && "event_type" in property) {
      const eventType = eventSchemas?.find(
        (event) => event.event_type === property.event_type,
      );

      return eventType ? getEventIcon(eventType) : EVENT_FALLBACK_ICON;
    }

    return getSchemaTypeIcon(
      property.type as Parameters<typeof getSchemaTypeIcon>[0],
    );
  };
};

const isPropertyWithType = (
  property: CaseProperty,
): property is CasePropertiesValue | CaseTemplateAdditionalInput => {
  return "type" in property;
};
